import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import classnames from "classnames";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { languageDisplayNames } from "sj-ui-toolkit/src/constants/content";
import {
  getDueDateLabel,
  convertDurationToMinutes,
  type CategoryItem,
} from "@/helpers/knowledgeAssessments";
import type { AssessmentFormValues } from "@/api/assessments";

interface ValidationErrors {
  metadata: {
    name: { hasError: boolean; message?: string };
    description: { hasError: boolean; message?: string };
  };
  audience: { hasError: boolean; message?: string };
  content: { hasError: boolean; message?: string };
  settings: {
    dates: { hasError: boolean; message?: string };
  };
}

interface AssessmentFormReviewProps {
  formValues: AssessmentFormValues;
  categories: CategoryItem[];
  startDate: Date | string | undefined;
  endDate: Date | string | undefined;
  onEdit: (step: number) => void;
  validationErrors: ValidationErrors;
  isFormValid: boolean;
  showValidationMessages: boolean;
}

function SectionHeader({
  number,
  title,
  onEdit,
  errorId,
  errorTooltip,
  hasError,
}: {
  number: number;
  title: string;
  onEdit: () => void;
  errorId: string;
  errorTooltip: React.ReactNode;
  hasError: boolean;
}) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <h4 className="font-semibold text-xl flex-1">
        {number}. {title}
      </h4>
      {hasError && (
        <span>
          <Tooltip target={`#${errorId}`} position="right">
            {errorTooltip}
          </Tooltip>
          <i id={errorId} className={classnames("pi pi-info-circle", themeClasses.iconError)} />
        </span>
      )}
      <Button label="Edit" onClick={onEdit} link />
    </div>
  );
}

export default function AssessmentFormReview({
  formValues,
  categories,
  startDate,
  endDate,
  onEdit,
  validationErrors,
  isFormValid,
  showValidationMessages,
}: AssessmentFormReviewProps) {
  const users = formValues.target.users;
  const newHire = formValues.newHire ?? false;
  const item = formValues.items[0];
  const ka = item?.content.knowledgeAssessment;
  const categoryValues = ka?.filters.sections ?? [];
  const selectedCategories = categories.filter(c => categoryValues.includes(c.path));
  const languages = ka?.parameters.allowedProgrammingLanguages ?? [];
  const hasLanguages = languages.length > 0;
  const timeLimit = ka?.timeLimit;
  const isDynamic = item?.dates?.dynamic;
  const notifyWhenAvailable = item?.notify?.notifyWhenAvailable;
  const sendReminders = item?.notify?.sendReminders;
  const strictAssignment = item?.strict;

  const languageText = hasLanguages
    ? languages.map(l => languageDisplayNames[l] ?? l).join(", ")
    : "Allow learners to self-select language.";

  const dueDateInfo = getDueDateLabel({
    isDynamic,
    endDate,
    days: item?.dates?.dueAfter?.days,
  });

  const cardClass = classnames(themeClasses.bgElevation2, "min-h-52");

  return (
    <>
      <div className="grid xl:grid-cols-2 gap-4">
        {/* 1. Name & Description */}
        <Card className={cardClass}>
          <SectionHeader
            number={1}
            title="Name & Description"
            onEdit={() => onEdit(0)}
            errorId="metadata-validation-error"
            hasError={
              !!validationErrors.metadata.name.hasError ||
              !!validationErrors.metadata.description.hasError
            }
            errorTooltip={
              <ul className="list-disc pl-4">
                {validationErrors.metadata.name.hasError && (
                  <li>{validationErrors.metadata.name.message}</li>
                )}
                {validationErrors.metadata.description.hasError && (
                  <li>{validationErrors.metadata.description.message}</li>
                )}
              </ul>
            }
          />
          <p className="mb-3">
            <span className="font-semibold">Assessment Name: </span>
            {formValues.listing?.title}
          </p>
          <p>
            <span className="font-semibold">Assessment Description: </span>
            {formValues.listing?.description}
          </p>
        </Card>

        {/* 2. Audience */}
        <Card className={cardClass}>
          <SectionHeader
            number={2}
            title="Audience"
            onEdit={() => onEdit(1)}
            errorId="audience-validation-error"
            hasError={!!validationErrors.audience.hasError}
            errorTooltip={validationErrors.audience.message}
          />
          {users.length > 0 ? (
            <p className="mb-3">
              <span className="font-semibold">
                {users.length} {users.length === 1 ? "Learner" : "Learners"}
              </span>{" "}
              added to the Assessment
            </p>
          ) : (
            <p className="mb-3">No learners selected</p>
          )}
          {newHire && (
            <p>
              <span className="font-semibold">New Hire Enabled:</span> All new users added in the
              platform will automatically be added to this assignment
            </p>
          )}
        </Card>

        {/* 3. Content */}
        <Card
          className={classnames(themeClasses.bgElevation2, "mt-0 min-h-72 max-h-96 overflow-auto")}
        >
          <SectionHeader
            number={3}
            title="Content"
            onEdit={() => onEdit(2)}
            errorId="content-validation-error"
            hasError={!!validationErrors.content.hasError}
            errorTooltip={validationErrors.content.message}
          />
          {selectedCategories.length > 0 ? (
            <>
              <p className="font-semibold mb-2">Content Categories selected:</p>
              {selectedCategories.map(category => (
                <div key={category.path}>
                  <p>{category.title ?? ""}</p>
                  {Array.isArray(category.programmingLanguages) &&
                    category.programmingLanguages.length > 0 && (
                      <p className="ml-4 body-sm">{`• Languages: ${languageText}`}</p>
                    )}
                </div>
              ))}
            </>
          ) : (
            <p>No categories selected</p>
          )}
          {timeLimit && (
            <p className="mt-3">
              <span className="font-semibold">Time Limit: </span>
              {convertDurationToMinutes(timeLimit)} minutes
            </p>
          )}
        </Card>

        {/* 4. Settings */}
        <Card className={cardClass}>
          <SectionHeader
            number={4}
            title="Assignment Settings"
            onEdit={() => onEdit(3)}
            errorId="settings-validation-error"
            hasError={!!validationErrors.settings.dates.hasError}
            errorTooltip={validationErrors.settings.dates.message}
          />
          <p className="mb-2">
            <span className="font-semibold">Start Date: </span>
            {startDate ? new Date(startDate).toLocaleDateString() : "Not set"}
          </p>
          {dueDateInfo && (
            <p className="mb-2">
              <span className="font-semibold">{dueDateInfo.label}: </span>
              {dueDateInfo.value}
            </p>
          )}
          {notifyWhenAvailable && (
            <p className="mb-2">
              <span className="font-semibold">Notify Learners: </span>
              When the assessment becomes available
            </p>
          )}
          {sendReminders && sendReminders !== "None" && (
            <p className="mb-2">
              <span className="font-semibold">Reminders: </span>
              {sendReminders}
            </p>
          )}
          {strictAssignment && (
            <p>
              <span className="font-semibold">Strict Assignment: </span>
              All other content is locked until this assessment is completed
            </p>
          )}
        </Card>
      </div>

      {showValidationMessages && !isFormValid && (
        <div className="flex justify-end mt-4">
          <ValidationMessage
            severity="warn"
            text="Please fill in all required fields before submitting"
          />
        </div>
      )}
    </>
  );
}
